/**
 * EventDetailPage
 *
 * Full-screen replacement for the old EventDetailModal. Same responsibilities
 * (view event, manage attendees, lifecycle transitions) plus:
 *   - Edit-in-place for title, description, time, duration, location_type,
 *     location_address — only when status='scheduled'/'confirmed'/'tentative'
 *     (the freeze guard in the BE will 409 attempts on terminal events).
 *   - Mark Complete (terminal, freezes the event)
 *   - Archive (only reachable from completed/cancelled — hides from default
 *     list views)
 *
 * Rendered as a page (not a modal). Parent passes onBack to return to the
 * list view; we don't own routing.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft, CheckCircle2, XCircle, HelpCircle, UserPlus, Trash2,
  Loader2, AlertTriangle, Save, Edit3, Lock, Archive, Check, Ban,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  calendarAPI, CalendarEvent, Attendee, ResponseStatus,
  LocationType, isEventFrozen,
} from '@/services/api/calendar';
import { authFetchJson } from '@/lib/authFetch';

interface EventDetailPageProps {
  eventId: number;
  /** Return to the list view. */
  onBack: () => void;
  /** Notify parent so the list refetches after archives/cancellations. */
  onMutated?: () => void;
}

interface StaffOption {
  user_id: number;
  name: string;
  email?: string;
  role?: string;
}

interface EditDraft {
  title: string;
  description: string;
  date: string; // yyyy-mm-dd local
  start_time: string; // HH:MM local
  duration_minutes: number;
  location_type: LocationType;
  location_address: string;
}

const LOCATION_TYPE_LABEL: Record<LocationType, string> = {
  in_person: 'In-Person',
  virtual: 'Virtual',
  phone: 'Phone',
  hybrid: 'Hybrid',
};

export const EventDetailPage: React.FC<EventDetailPageProps> = ({
  eventId,
  onBack,
  onMutated,
}) => {
  const [event, setEvent] = useState<CalendarEvent | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [staffPool, setStaffPool] = useState<StaffOption[] | null>(null);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<EditDraft | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    calendarAPI
      .getEvent(eventId)
      .then((d) => setEvent(d))
      .catch((e: any) => setError(e?.message || 'Failed to load event'))
      .finally(() => setLoading(false));
  }, [eventId]);

  useEffect(() => {
    if (!showAdd || staffPool) return;
    const cboId = Number(localStorage.getItem('active_cbo_id') || 0);
    if (!cboId) return;
    authFetchJson<any[]>(`/api/organization-setup/${cboId}/staff?exclude_admins=true`)
      .then((rows) => {
        setStaffPool(
          (rows || [])
            .filter((r) => r.is_active !== false)
            .map((r) => ({
              user_id: r.user_id ?? r.id,
              name: r.name || `${r.first_name || ''} ${r.last_name || ''}`.trim() || r.email,
              email: r.email,
              role: r.role || r.role_display_name,
            }))
            .filter((s) => s.user_id),
        );
      })
      .catch(() => setStaffPool([]));
  }, [showAdd, staffPool]);

  const attendees: Attendee[] = (event?.attendees as any) || [];
  const existingUserIds = useMemo(
    () => new Set(attendees.map((a) => a.user_id).filter((id): id is number => !!id)),
    [attendees],
  );
  const availableToAdd = (staffPool || []).filter((s) => !existingUserIds.has(s.user_id));

  const frozen = isEventFrozen(event?.status);
  const subtype = (event?.custom_fields as any)?.activity_subtype;

  const refresh = async () => {
    const d = await calendarAPI.getEvent(eventId);
    setEvent(d);
    onMutated?.();
  };

  const beginEdit = () => {
    if (!event) return;
    const start = new Date(event.start_time);
    const end = new Date(event.end_time);
    const pad = (n: number) => String(n).padStart(2, '0');
    setDraft({
      title: event.title,
      description: event.description || '',
      date: `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`,
      start_time: `${pad(start.getHours())}:${pad(start.getMinutes())}`,
      duration_minutes: Math.max(15, Math.round((end.getTime() - start.getTime()) / 60000)),
      location_type: event.location_type,
      location_address: event.location_address || '',
    });
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft(null);
  };

  const saveEdit = async () => {
    if (!draft) return;
    const start = new Date(`${draft.date}T${draft.start_time}:00`);
    if (isNaN(start.getTime())) {
      setError('Invalid date / time.');
      return;
    }
    const end = new Date(start.getTime() + draft.duration_minutes * 60_000);
    setActing(true);
    setError(null);
    try {
      await calendarAPI.updateEvent(eventId, {
        title: draft.title.trim(),
        description: draft.description.trim() || undefined,
        start_time: start.toISOString(),
        end_time: end.toISOString(),
        location_type: draft.location_type,
        location_address: draft.location_address.trim() || undefined,
      });
      await refresh();
      setEditing(false);
      setDraft(null);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to save changes');
    } finally {
      setActing(false);
    }
  };

  const respond = async (attendeeId: number, status: ResponseStatus) => {
    setActing(true);
    try {
      await calendarAPI.respondToEvent(attendeeId, { response_status: status });
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to update response');
    } finally {
      setActing(false);
    }
  };

  const addAttendee = async (userId: number) => {
    setActing(true);
    try {
      await calendarAPI.addAttendee(eventId, {
        user_id: userId,
        attendee_role: 'attendee',
        is_optional: false,
      } as any);
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to add attendee');
    } finally {
      setActing(false);
    }
  };

  const removeAttendee = async (attendeeId: number) => {
    if (!confirm('Remove this attendee from the event?')) return;
    setActing(true);
    try {
      await calendarAPI.removeAttendee(attendeeId);
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to remove attendee');
    } finally {
      setActing(false);
    }
  };

  const markComplete = async () => {
    if (!confirm('Mark this event as complete? After this, it can no longer be edited.')) return;
    setActing(true);
    setError(null);
    try {
      await calendarAPI.completeEvent(eventId);
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to mark complete');
    } finally {
      setActing(false);
    }
  };

  const archive = async () => {
    if (!confirm('Archive this event? It will be hidden from default list views.')) return;
    setActing(true);
    setError(null);
    try {
      await calendarAPI.archiveEvent(eventId);
      onMutated?.();
      onBack();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to archive event');
    } finally {
      setActing(false);
    }
  };

  const cancelEvent = async () => {
    if (!confirm('Cancel this event? Attendees will be notified.')) return;
    setActing(true);
    setError(null);
    try {
      await calendarAPI.deleteEvent(eventId, true);
      onMutated?.();
      onBack();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to cancel event');
    } finally {
      setActing(false);
    }
  };

  const start = event?.start_time ? new Date(event.start_time) : null;
  const end = event?.end_time ? new Date(event.end_time) : null;
  const minutes = start && end ? Math.round((end.getTime() - start.getTime()) / 60000) : null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-4 sm:px-6 py-3">
        <div className="max-w-4xl mx-auto flex items-center gap-3 flex-wrap">
          <Button variant="ghost" size="sm" onClick={onBack} disabled={acting}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
            <h1 className="text-lg font-semibold truncate">{event?.title || 'Event'}</h1>
            {event && (
              <Badge variant="outline" className="text-xs">
                {event.event_type === 'network_hub_meeting' ? 'Hub Meeting' : event.event_type === 'training_workshop' ? 'Training/Workshop' : event.event_type}
              </Badge>
            )}
            {subtype && <Badge variant="outline" className="text-xs">{subtype}</Badge>}
            <StatusBadge status={event?.status} />
          </div>
          {event && !editing && !frozen && (
            <Button variant="outline" size="sm" onClick={beginEdit} disabled={acting}>
              <Edit3 className="w-3.5 h-3.5 mr-1" />
              Edit
            </Button>
          )}
          {event && editing && (
            <>
              <Button variant="outline" size="sm" onClick={cancelEdit} disabled={acting}>
                Cancel
              </Button>
              <Button size="sm" onClick={saveEdit} disabled={acting}>
                {acting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                Save
              </Button>
            </>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 sm:p-6 space-y-4">
        {loading && (
          <div className="text-sm text-gray-500 py-8 text-center inline-flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}
        {error && (
          <div className="flex gap-2 items-start text-sm bg-red-50 border border-red-200 text-red-900 rounded-md p-3">
            <AlertTriangle className="w-4 h-4 mt-0.5 text-red-600 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
        {frozen && event && !editing && (
          <div className="flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-md p-3 dark:bg-amber-900/20 dark:border-amber-700 dark:text-amber-200">
            <Lock className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-medium">
                This event is {event.status === 'completed' ? 'completed' : event.status === 'archived' ? 'archived' : event.status === 'cancelled' ? 'cancelled' : event.status}.
              </div>
              <div className="text-xs mt-0.5 opacity-80">
                Fields are frozen. Attendee responses can still be updated for record-keeping.
              </div>
            </div>
          </div>
        )}

        {event && !loading && (
          <>
            {/* Details card */}
            <Card>
              <CardContent className="p-4 space-y-4">
                {!editing ? (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className="text-[10px] uppercase tracking-wide text-gray-500 font-mono mb-0.5">When</div>
                        <div>{start ? start.toLocaleString() : '—'}{minutes != null && ` · ${minutes} min`}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wide text-gray-500 font-mono mb-0.5">Format</div>
                        <div>
                          {LOCATION_TYPE_LABEL[event.location_type] || event.location_type}
                          {event.location_address && <span className="text-gray-500"> · {event.location_address}</span>}
                        </div>
                      </div>
                    </div>
                    {event.description && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wide text-gray-500 font-mono mb-0.5">Topic / Description</div>
                        <div className="text-sm whitespace-pre-wrap">{event.description}</div>
                      </div>
                    )}
                  </>
                ) : draft ? (
                  <EditForm draft={draft} setDraft={setDraft} />
                ) : null}
              </CardContent>
            </Card>

            {/* Attendees */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-wide text-gray-500 font-mono">
                    Attendees ({attendees.length})
                  </div>
                  {!frozen && !editing && (
                    <Button variant="outline" size="sm" onClick={() => setShowAdd((s) => !s)}>
                      <UserPlus className="w-3.5 h-3.5 mr-1" />
                      Add attendee
                    </Button>
                  )}
                </div>
                {attendees.length === 0 ? (
                  <div className="text-xs text-gray-500">No attendees yet.</div>
                ) : (
                  <div className="space-y-1.5">
                    {attendees.map((a) => (
                      <AttendeeRow
                        key={a.id ?? `${a.user_id}-${a.external_email ?? ''}`}
                        attendee={a}
                        disabled={acting}
                        canRemove={!frozen}
                        onRespond={(status) => a.id != null && respond(a.id, status)}
                        onRemove={() => a.id != null && removeAttendee(a.id)}
                      />
                    ))}
                  </div>
                )}

                {showAdd && !frozen && (
                  <div className="mt-3 border border-gray-200 dark:border-gray-700 rounded-md p-3 bg-gray-50 dark:bg-gray-800/30">
                    <div className="text-xs font-medium mb-2">Add staff</div>
                    {!staffPool ? (
                      <div className="text-xs text-gray-500">Loading staff…</div>
                    ) : availableToAdd.length === 0 ? (
                      <div className="text-xs text-gray-500">No more staff to add.</div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-48 overflow-auto">
                        {availableToAdd.map((s) => (
                          <button
                            key={s.user_id}
                            type="button"
                            disabled={acting}
                            onClick={() => addAttendee(s.user_id)}
                            className="text-left text-xs px-2 py-1.5 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-700 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 truncate"
                          >
                            <span className="font-medium">{s.name}</span>
                            {s.role && <span className="text-gray-500 ml-1">· {s.role}</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Lifecycle actions */}
            {!editing && (
              <Card>
                <CardContent className="p-4">
                  <div className="text-[10px] uppercase tracking-wide text-gray-500 font-mono mb-2">Lifecycle</div>
                  <div className="flex flex-wrap gap-2">
                    {/* Mark complete — available from any non-terminal state */}
                    {!frozen && (
                      <Button variant="outline" size="sm" onClick={markComplete} disabled={acting}>
                        <Check className="w-3.5 h-3.5 mr-1" />
                        Mark complete
                      </Button>
                    )}
                    {/* Cancel — available from non-terminal */}
                    {!frozen && (
                      <Button variant="outline" size="sm" onClick={cancelEvent} disabled={acting}>
                        <Ban className="w-3.5 h-3.5 mr-1" />
                        Cancel event
                      </Button>
                    )}
                    {/* Archive — only from completed/cancelled */}
                    {(event.status === 'completed' || event.status === 'cancelled') && (
                      <Button variant="outline" size="sm" onClick={archive} disabled={acting}>
                        <Archive className="w-3.5 h-3.5 mr-1" />
                        Archive
                      </Button>
                    )}
                    {event.status === 'archived' && (
                      <span className="text-xs text-gray-500 self-center">Already archived. No further actions.</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>
    </div>
  );
};

const EditForm: React.FC<{ draft: EditDraft; setDraft: (d: EditDraft) => void }> = ({ draft, setDraft }) => {
  const set = <K extends keyof EditDraft>(k: K, v: EditDraft[K]) => setDraft({ ...draft, [k]: v });
  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs text-gray-500 block mb-1">Title</label>
        <Input value={draft.title} onChange={(e) => set('title', e.target.value)} />
      </div>
      <div>
        <label className="text-xs text-gray-500 block mb-1">Topic / Description</label>
        <Textarea rows={3} value={draft.description} onChange={(e) => set('description', e.target.value)} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="text-xs text-gray-500 block mb-1">Date</label>
          <Input type="date" value={draft.date} onChange={(e) => set('date', e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-gray-500 block mb-1">Start time</label>
          <Input type="time" value={draft.start_time} onChange={(e) => set('start_time', e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-gray-500 block mb-1">Duration (min)</label>
          <Input
            type="number"
            min={15}
            step={15}
            value={draft.duration_minutes}
            onChange={(e) => set('duration_minutes', Math.max(15, Number(e.target.value) || 15))}
          />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-500 block mb-1">Format</label>
          <select
            value={draft.location_type}
            onChange={(e) => set('location_type', e.target.value as LocationType)}
            className="block w-full border border-gray-300 dark:border-gray-700 dark:bg-gray-900 rounded-md px-3 py-2 text-sm"
          >
            <option value="in_person">In-Person</option>
            <option value="virtual">Virtual</option>
            <option value="phone">Phone</option>
            <option value="hybrid">Hybrid</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500 block mb-1">Location address / link</label>
          <Input
            autoComplete="street-address"
            value={draft.location_address}
            onChange={(e) => set('location_address', e.target.value)}
            placeholder={draft.location_type === 'virtual' ? 'Meeting link or notes' : 'Street address'}
          />
        </div>
      </div>
    </div>
  );
};

const StatusBadge: React.FC<{ status?: string }> = ({ status }) => {
  if (!status || status === 'scheduled' || status === 'confirmed' || status === 'tentative') return null;
  const styleMap: Record<string, string> = {
    completed: 'bg-green-50 text-green-700 border-green-200',
    cancelled: 'bg-red-50 text-red-700 border-red-200',
    archived: 'bg-gray-100 text-gray-700 border-gray-300',
    no_show: 'bg-orange-50 text-orange-700 border-orange-200',
    rescheduled: 'bg-blue-50 text-blue-700 border-blue-200',
  };
  return (
    <Badge variant="outline" className={`text-xs capitalize ${styleMap[status] || ''}`}>
      {status.replace('_', ' ')}
    </Badge>
  );
};

const AttendeeRow: React.FC<{
  attendee: Attendee;
  disabled: boolean;
  canRemove: boolean;
  onRespond: (status: ResponseStatus) => void;
  onRemove: () => void;
}> = ({ attendee, disabled, canRemove, onRespond, onRemove }) => {
  const status = attendee.response_status;
  return (
    <div className="flex items-center justify-between gap-2 bg-white dark:bg-gray-900 rounded px-2.5 py-1.5 border border-gray-100 dark:border-gray-700">
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-sm truncate">
          {(attendee as any).attendee_name || attendee.external_name || attendee.external_email || `user #${attendee.user_id}`}
        </span>
        <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
          {status || 'pending'}
        </Badge>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          type="button"
          disabled={disabled}
          onClick={() => onRespond('accepted')}
          className={`p-1 rounded hover:bg-green-50 ${status === 'accepted' ? 'text-green-700' : 'text-gray-400'}`}
          title="Mark accepted"
        >
          <CheckCircle2 className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onRespond('tentative')}
          className={`p-1 rounded hover:bg-amber-50 ${status === 'tentative' ? 'text-amber-700' : 'text-gray-400'}`}
          title="Mark tentative"
        >
          <HelpCircle className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onRespond('declined')}
          className={`p-1 rounded hover:bg-red-50 ${status === 'declined' ? 'text-red-700' : 'text-gray-400'}`}
          title="Mark declined"
        >
          <XCircle className="w-3.5 h-3.5" />
        </button>
        {canRemove && (
          <button
            type="button"
            disabled={disabled}
            onClick={onRemove}
            className="p-1 rounded hover:bg-red-50 text-gray-400"
            title="Remove attendee"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default EventDetailPage;
